/* @flow */

import { DateTime, Settings } from 'luxon';
import {
  getBraindateId,
  getBraindateConversation,
  getBraindateCheckInStatus,
  getBraindateStartTime,
} from '@braindate/domain/lib/braindate/util';
import { getConversationParticipants } from '@braindate/domain/lib/conversation/util';
import { getUserId } from '@braindate/domain/lib/user/util';
import { assertObject } from '@braindate/util/lib/assert';

import CheckInNotification from 'src/shared/app/check-in/component/notification/CheckInNotification';
import CheckInTakeOver from 'src/shared/app/check-in/component/notification/CheckInTakeOver';
import { takeOverMinutesToBraindate } from 'src/shared/app/check-in/settings/checkInSettings';
import {
  braindatesRoute,
  braindateRoute,
} from 'src/shared/app/base/route/setting/routeSettings';
import {
  notificationTakeOverType,
  notificationInfoLevel,
  notificationTopType,
} from 'src/shared/app/notification/constant/notificationConstants';

import type { Braindate } from '@braindate/domain/lib/braindate/type';
import type { User } from '@braindate/domain/lib/user/type';
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';

export function getCheckedInParticipants(braindate: Braindate): Array<User> {
  const conversation = getBraindateConversation(braindate);
  const participants = getConversationParticipants(conversation);
  const checkedInState = getBraindateCheckInStatus(braindate) || [];

  return participants.filter((user) =>
    checkedInState.find((data) => data.id === getUserId(user)),
  );
}

export function getMissingParticipants(braindate: Braindate): Array<User> {
  const conversation = getBraindateConversation(braindate);
  const participants = getConversationParticipants(conversation);
  const checkedInState = getBraindateCheckInStatus(braindate) || [];

  return participants.filter(
    (user) => !checkedInState.find((data) => data.id === getUserId(user)),
  );
}

export function getCheckedInOtherParticipants(
  braindate: Braindate,
  selfUser: User,
): Array<User> {
  return getCheckedInParticipants(braindate).filter(
    (user) => getUserId(user) !== getUserId(selfUser),
  );
}

export function getMissingOtherParticipants(
  braindate: Braindate,
  selfUser: User,
): Array<User> {
  return getMissingParticipants(braindate).filter(
    (user) => getUserId(user) !== getUserId(selfUser),
  );
}

export function hasUserCheckedIn(braindate: Braindate, user: User): boolean {
  const status = getBraindateCheckInStatus(braindate) || [];

  return !!status.find((data) => data.id === getUserId(user));
}

export function hasSomeoneCheckedIn(
  braindate: Braindate,
  selfUser: User,
): boolean {
  assertObject(braindate, 'braindate');
  assertObject(selfUser, 'selfUser');

  const checkedInPax = getCheckedInOtherParticipants(braindate, selfUser) || [];

  return !!checkedInPax.length;
}

export function getBraindateReminderNotificationId(
  braindate: Braindate,
): string {
  return `braindate-reminder-${getBraindateId(braindate)}`;
}

export function showCheckInTakeOver(braindate: Braindate): boolean {
  const startTime = getBraindateStartTime(braindate);
  const startDateTime = DateTime.fromISO(startTime, {
    zone: Settings.defaultZone,
  });
  const takeOverDateTime = startDateTime.minus({
    minutes: takeOverMinutesToBraindate,
  });

  return takeOverDateTime.diffNow().milliseconds < 0;
}

export function convertBraindateReminderToNotification(
  braindate: Braindate,
): Notification<> {
  const showTakeOver = showCheckInTakeOver(braindate);

  return {
    id: getBraindateReminderNotificationId(braindate),
    level: notificationInfoLevel,
    type: showTakeOver ? notificationTakeOverType : notificationTopType,
    componentClass: showTakeOver ? CheckInTakeOver : CheckInNotification,
    data: braindate,
    exclude: [braindatesRoute, braindateRoute],
  };
}
